// Ring tiers per device (APP_HORIZON.md §8c): which level of the far ring a
// device generates, sector by sector. PURE (no three, no fetch): ring.js
// buildRingTex runs it, scripts/check-ring.mjs pins it (the class table
// fixture scripts/fixtures/ring-tiers.json, the refusal, a red twin per rule).
//
// The bake (meta.panorama: the far field's data contract — the core file
// with the world line + the far-height sampler — and meta.panorama.classes,
// format 'ring-classes-2') cuts the ring into sectors, each with three
// generation inputs (cls: the class raster, lit: sun / sky visibility, res:
// the 64 m residual; level-independent, the same game-space rect; cls / res
// raw u8 bytes with their `sha`, never an image: ringref ringExact). The
// device paints the sector's texture itself (ringgen.js, gensched pass 'ring')
// into the ONE sector atlas (faratlas.js): level l = the class raster's px >>
// l per side, its texel = cell_m[tier] x 2^l / gen.up (ringtiers.json gen).
// The far MESH is the ground tree's outer nodes (H1): no ring mesh, no
// panorama.tiles. A bake without the contract (an older ring-mesh bake:
// panorama.tiles, no core) has no far ring the client can draw, and sky.js
// buildPanorama throws on it.
//
// THE RULES (each one a function below, each one a check-ring row):
//   ringTilesFault  '' only with the core (file + the world line and sampler
//              offsets), the sampler, the world line and the classes block of
//              the known format with a cls / lit / res file (cls / res with a
//              sha16), a tier, px and rect for every sector; anything else (an
//              older bake, a half-written meta) = the first thing missing,
//              which buildPanorama throws.
//   genLevel   the COARSEST level (of genLevels: l while min(px) >> l >= 64,
//              level 0 always) whose texel at the eye's horizontal distance
//              to the sector, texel / d, is <= k x the device's pixel angle;
//              no eye (Map first, before a target): the coarsest everywhere.
//   fits       the planned set packs into the sector atlas (faratlas.js
//              budget.fits([{ px, level }]), the dry-run pack): while it does
//              not, the FARTHEST sector coarsens first, level by level to its
//              coarsest, then the next farthest — no further than fits needs
//              (the shortest such prefix: fits is taken as monotone along it).
//   pixel angle 2 tan(vfov / 2) / px — the centre pixel of the view; flat /
//              mobile measure it live (camera fov, drawing-buffer height), VR
//              and the fixture read ringtiers.json classes.
//   keep       never downgrade: a resident level finer than the plan stays
//              (ring.js keeps it wanted; a coarser one finishing late is
//              dropped, ring.commit) — while the kept set still fits the
//              atlas: else the farthest kept sectors return to the plan first.
//   withinPixel the one size-vs-pixel comparison (genLevel; the asset
//              queue's need score drops what it says fits in one pixel).
import RT from './ringtiers.json' with { type: 'json' };

export const RING_CLASSES_FORMAT = 'ring-classes-2';
// ringtiers.json gen: up = generated texels per class cell (per side)
export const RING_GEN = RT.gen;
if (!(RING_GEN && RING_GEN.up > 0)) throw new Error('ringtiers.json: no gen {up}');
// the coarsest level keeps >= this many texels on its short side
export const RING_GEN_MIN_PX = 64;

const isOff = (v) => Number.isInteger(v) && v >= 0;
// what keeps meta.panorama from being the far field's data contract + classes of
// RING_CLASSES_FORMAT ('' = nothing: buildRingTex can load it)
export function ringTilesFault(pano) {
  if (!pano) return 'no meta.panorama';
  const k = pano.core, c = pano.classes;
  if (!k || !k.file) return `panorama has no core file${pano.tiles ? ' (an old ring-mesh world: rebuild it, Build)' : ''}`;
  if (!(k.border && isOff(k.border.off) && k.sampler && isOff(k.sampler.off))) return 'panorama.core has no world line / sampler offsets';
  const sm = pano.sampler;
  if (!(sm && sm.n > 1 && sm.step_posts > 0)) return 'panorama has no sampler {n, step_posts}';
  const b = pano.border;
  if (!(b && b.n > 0 && b.unit_m > 0 && Array.isArray(b.frame_m))) return 'panorama has no world line {n, unit_m, frame_m}';
  if (!c || c.format !== RING_CLASSES_FORMAT) {
    return `panorama.classes is not ${RING_CLASSES_FORMAT}${pano.texture ? ' (an old ring-texture world: rebuild it, Build)' : ''}`;
  }
  if (!c.dir || !c.sectors || !Object.keys(c.sectors).length) return 'panorama.classes has no sectors';
  for (const [id, q] of Object.entries(c.sectors)) {
    if (!q) return `sector ${id} has no panorama.classes entry`;
    if (!(c.cell_m && c.cell_m[q.tier] > 0)) return `sector ${id} has no tier with a cell_m`;
    if (!(Array.isArray(q.px) && q.px.length === 2 && Array.isArray(q.rect) && q.rect.length === 4)) return `sector ${id} has no px / rect`;
    for (const f of ['cls', 'lit', 'res']) if (!q[f] || !q[f].file) return `sector ${id} has no ${f} file`;
    for (const f of ['cls', 'res']) if (!/^[0-9a-f]{16}$/.test(q[f].sha || '')) return `sector ${id} ${f} has no sha`;
  }
  return '';
}

// the ring waits until the player STANDS (APP_STARTUP: the far ring is never needed to stand):
// false through the beam (sched phase 'beam': the latch not fired) and while the start-up overlay
// is up before the latch (sched hidden); ph / hid = sched phase() / hidden() (the pass's ready)
export const ringLatched = (ph, hid) => ph === 'play' || (ph !== 'beam' && !hid);

// radians per pixel at the centre of a view vfov_deg tall over px pixels
export function pixelAngle(vfovDeg, px) {
  return 2 * Math.tan((vfovDeg * Math.PI) / 360) / Math.max(1, px);
}

// ringtiers.json classes[cls] (or default_class) -> pixel angle
export function classPixelAngle(cfg, cls) {
  const c = (cfg.classes || {})[cls] || (cfg.classes || {})[cfg.default_class];
  return c ? pixelAngle(c.vfov_deg, c.px) : null;
}

// horizontal distance from (x, z) to a rect [x0, z0, x1, z1] (0 inside)
export function rectDist(x, z, r) {
  const dx = Math.max(r[0] - x, 0, x - r[2]);
  const dz = Math.max(r[1] - z, 0, z - r[3]);
  return Math.hypot(dx, dz);
}

// THE pixel rule (one rule, two readers): m metres at horizontal distance
// d subtend at most k pixels of angle pa — genLevel's "this texel is fine
// enough" and the asset queue's need score (needscore.js: an asset whose
// whole extent is within one pixel is never fetched)
export function withinPixel(m, d, pa, k = 1) {
  return m / Math.max(d, 1) <= k * pa;
}

// the level-0 generated texel (m) of a tier: classes.cell_m[tier] / gen.up
export function genTexel(classes, tier) {
  const c = classes.cell_m && classes.cell_m[tier];
  if (!(c > 0)) throw new Error(`panorama.classes has no cell_m for tier ${tier}`);
  return c / RING_GEN.up;
}

// how many levels a sector's class raster of px [w, h] has: l = 0.. while
// min(w, h) >> l >= RING_GEN_MIN_PX (level 0 always)
export function genLevels(px) {
  let n = 1;
  while (Math.min(px[0], px[1]) >> n >= RING_GEN_MIN_PX) n++;
  return n;
}

// of n levels (0 = finest, texel cell x 2^l), the coarsest whose texel angle
// at distance d is <= k x pa; d == null: the coarsest
export function genLevel(cell, n, d, pa, k = 1) {
  if (d == null || !(pa > 0)) return n - 1;
  let pick = 0;
  for (let i = 1; i < n; i++) {
    const m = cell * 2 ** i;
    if (withinPixel(m, d, pa, k)) pick = i;
    else break;
  }
  return pick;
}

// the atlas list of a plan ([{ px, level }], faratlas budget.fits'), level = lv(id, p)
const listOf = (plan, lv = (id, p) => p.tex) => [...plan].map(([id, p]) => ({ px: p.px, level: lv(id, p) }));

// the plan: Map sector id -> { tex (the generated level), d (the eye's distance, Infinity at boot),
// px (the class raster), n (its levels) }.
//   classes meta.panorama.classes
//   eye     {x, z} or null (boot: coarsest everywhere)
//   pa      the view's pixel angle (rad); k pixels per texel (1)
//   fits    null (no atlas: no ceiling) or the atlas's budget.fits([{ px, level }])
// then the fits rule: the farthest sector coarsens first, the shortest prefix of those steps that fits
// IN STEPS (xr-stalls): planRingSteps / wantsSteps are the same rules as step generators —
// `fits` may answer a boolean or a step generator of one (faratlas budget.fitsSteps: a step
// per packed box), yielded through — so ring.js runs a re-plan on the frame budget (a dry-run
// pack is ~4 ms on a desktop, ~9 per re-plan: 100-150 ms of one Quest frame, telemetry
// 'focus'). planRing / wants = their drains: the same plan.
function* fitsOf(fits, list) {
  const r = fits(list);
  return r && typeof r.next === 'function' ? yield* r : r;
}
const drainSteps = (it) => { for (;;) { const s = it.next(); if (s.done) return s.value; } };
export function planRing(classes, opts = {}) { return drainSteps(planRingSteps(classes, opts)); }
export function* planRingSteps(classes, { eye = null, pa, k = 1, fits = null } = {}) {
  const plan = new Map();
  for (const [id, q] of Object.entries(classes.sectors)) {
    const cell = genTexel(classes, q.tier), n = genLevels(q.px);
    const d = eye ? rectDist(eye.x, eye.z, q.rect) : null;
    plan.set(id, { tex: genLevel(cell, n, d, pa, k), d: d == null ? Infinity : d, px: q.px, n });
  }
  if (!fits || (yield* fitsOf(fits, listOf(plan)))) return plan;
  // the steps: farthest first, each sector level by level to its coarsest
  const steps = [];
  for (const [id, p] of [...plan].sort((a, b) => b[1].d - a[1].d || (a[0] < b[0] ? -1 : 1))) {
    for (let l = p.tex + 1; l < p.n; l++) steps.push([id, l]);
  }
  const after = (s) => {
    const lv = new Map();
    for (let i = 0; i < s; i++) lv.set(steps[i][0], steps[i][1]);
    return lv;
  };
  // the shortest prefix that fits (bisection: one dry-run pack per halving)
  let lo = 0, hi = steps.length;
  const all = after(hi);
  if (yield* fitsOf(fits, listOf(plan, (id, p) => all.get(id) ?? p.tex))) {
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1, lv = after(mid);
      if (yield* fitsOf(fits, listOf(plan, (id, p) => lv.get(id) ?? p.tex))) hi = mid; else lo = mid;
    }
  }
  for (const [id, l] of after(hi)) plan.get(id).tex = l;
  return plan;
}

// keep: what to GENERATE given what is resident — a finer resident level
// (lower index) is never replaced by a coarser one while the kept set fits.
// loaded: Map id -> { tex (level, -1 = none) }; fits: as planRing's (null: no
// ceiling). Returns Map id -> { tex: level to generate or -1 (keep the resident) }
export function wants(plan, loaded, fits = null) { return drainSteps(wantsSteps(plan, loaded, fits)); }
export function* wantsSteps(plan, loaded, fits = null) {
  const out = new Map();
  for (const [id, p] of plan) {
    const l = loaded.get(id) || { tex: -1 };
    out.set(id, { tex: l.tex >= 0 && l.tex <= p.tex ? -1 : p.tex });
  }
  if (!fits) return out;
  const lvl = (id, p) => (out.get(id).tex < 0 ? loaded.get(id).tex : p.tex);
  // over the atlas: the farthest kept-finer sectors go back to their plan level first
  const kept = [...plan].filter(([id, p]) => out.get(id).tex < 0 && loaded.get(id).tex < p.tex).sort((a, b) => b[1].d - a[1].d);
  for (const [id, p] of kept) {
    if (yield* fitsOf(fits, listOf(plan, lvl))) break;
    out.set(id, { tex: p.tex });
  }
  return out;
}

// the bytes a plan downloads: every sector's generation inputs once (cls / lit / res: level-
// independent, panorama.classes.bytes) — the far MESH is the ground tree's (its own need score /
// budget). size(file, kind) -> bytes on the wire (kind 'cls' | 'lit' | 'res': a caller may charge .br)
export function planBytes(classes, size) {
  const r = { cls: 0, lit: 0, res: 0 };
  for (const q of Object.values(classes.sectors)) for (const k of ['cls', 'lit', 'res']) r[k] += size(q[k].file, k);
  return { ...r, total: r.cls + r.lit + r.res };
}
